import React, { createContext, useContext, useState, useEffect } from 'react';
import localforage from 'localforage';

/**
 * Scenario Context for managing multiple simulation scenarios
 * Provides state management for scenario selection, comparison, and switching
 */

const ScenarioContext = createContext(null);

// Configure localforage for scenario storage
const scenarioStore = localforage.createInstance({
  name: 'fjernvarme-skaelskoer',
  storeName: 'scenarios',
});

export const ScenarioProvider = ({ children }) => {
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load scenarios from storage on mount
  useEffect(() => {
    loadScenariosFromStorage();
  }, []);

  const loadScenariosFromStorage = async () => {
    try {
      setLoading(true);
      const keys = await scenarioStore.keys();
      const loadedScenarios = [];
      
      for (const key of keys) {
        const scenario = await scenarioStore.getItem(key);
        if (scenario) {
          loadedScenarios.push(scenario);
        }
      }
      
      // Sort by timestamp (newest first)
      loadedScenarios.sort((a, b) => 
        new Date(b.timestamp) - new Date(a.timestamp)
      );
      
      setScenarios(loadedScenarios);
      
      // Set first scenario as active if none is set
      if (loadedScenarios.length > 0 && !activeScenarioId) {
        setActiveScenarioId(loadedScenarios[0].id);
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const createScenario = async (name, config, description = '') => {
    const scenario = {
      id: `scenario-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      description,
      timestamp: new Date().toISOString(),
      config,
      results: null, // Will be populated after simulation
    };

    try {
      await scenarioStore.setItem(scenario.id, scenario);
      setScenarios(prev => [scenario, ...prev]);
      setActiveScenarioId(scenario.id);
      return scenario;
    } catch (error) {
      console.error('Failed to create scenario:', error);
      throw error;
    }
  };

  const updateScenario = async (scenarioId, updates) => {
    try {
      const scenario = scenarios.find(s => s.id === scenarioId);
      if (!scenario) {
        throw new Error('Scenario not found');
      }

      const updated = {
        ...scenario,
        ...updates,
        updatedAt: new Date().toISOString(),
      };

      await scenarioStore.setItem(scenarioId, updated);
      setScenarios(prev =>
        prev.map(s => (s.id === scenarioId ? updated : s))
      );
      return updated;
    } catch (error) {
      console.error('Failed to update scenario:', error);
      throw error;
    }
  };

  const deleteScenario = async (scenarioId) => {
    try {
      await scenarioStore.removeItem(scenarioId);
      setScenarios(prev => prev.filter(s => s.id !== scenarioId));
      
      // If deleted scenario was active, set first remaining as active
      if (activeScenarioId === scenarioId) {
        const remaining = scenarios.filter(s => s.id !== scenarioId);
        setActiveScenarioId(remaining.length > 0 ? remaining[0].id : null);
      }
      
      // Remove from comparison selection if present
      setSelectedScenarioIds(prev => prev.filter(id => id !== scenarioId));
    } catch (error) {
      console.error('Failed to delete scenario:', error);
      throw error;
    }
  };

  const duplicateScenario = async (scenarioId, newName) => {
    const original = scenarios.find(s => s.id === scenarioId);
    if (!original) {
      throw new Error('Scenario not found');
    }

    return createScenario(
      newName || `${original.name} (kopi)`,
      { ...original.config },
      original.description
    );
  };

  const setActiveScenario = (scenarioId) => {
    if (scenarios.some(s => s.id === scenarioId)) {
      setActiveScenarioId(scenarioId);
    }
  };

  const getActiveScenario = () => {
    return scenarios.find(s => s.id === activeScenarioId) || null;
  };

  const getScenario = (scenarioId) => {
    return scenarios.find(s => s.id === scenarioId) || null;
  };

  const exportScenario = (scenarioId) => {
    const scenario = getScenario(scenarioId);
    if (!scenario) {
      throw new Error('Scenario not found');
    }
    return JSON.stringify(scenario, null, 2);
  };

  const importScenario = async (jsonString) => {
    try {
      const scenario = JSON.parse(jsonString);
      
      // Validate scenario structure
      if (!scenario.name || !scenario.config) {
        throw new Error('Invalid scenario format');
      }

      // Generate new ID and timestamp
      const imported = {
        ...scenario,
        id: `scenario-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        name: `${scenario.name} (importeret)`,
      };

      await scenarioStore.setItem(imported.id, imported);
      setScenarios(prev => [imported, ...prev]);
      return imported;
    } catch (error) {
      console.error('Failed to import scenario:', error);
      throw error;
    }
  };

  const toggleComparisonMode = () => {
    setComparisonMode(prev => !prev);
    if (!comparisonMode && activeScenarioId) {
      setSelectedScenarioIds([activeScenarioId]);
    }
  };

  const toggleScenarioSelection = (scenarioId) => {
    setSelectedScenarioIds(prev => {
      if (prev.includes(scenarioId)) {
        return prev.filter(id => id !== scenarioId);
      } else {
        // Limit to 4 scenarios for comparison
        if (prev.length >= 4) {
          return prev;
        }
        return [...prev, scenarioId];
      }
    });
  };

  const value = {
    // State
    scenarios,
    activeScenarioId,
    comparisonMode,
    selectedScenarioIds,
    loading,
    
    // Methods
    createScenario,
    updateScenario,
    deleteScenario,
    duplicateScenario,
    setActiveScenario,
    getActiveScenario,
    getScenario,
    exportScenario,
    importScenario,
    toggleComparisonMode,
    toggleScenarioSelection,
    reloadScenarios: loadScenariosFromStorage,
  };

  return (
    <ScenarioContext.Provider value={value}>
      {children}
    </ScenarioContext.Provider>
  );
};

export const useScenarios = () => {
  const context = useContext(ScenarioContext);
  if (!context) {
    throw new Error('useScenarios must be used within ScenarioProvider');
  }
  return context;
};

export default ScenarioContext;
